package app.nestbridge.feature.profile.presentation

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.produceState
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.karawek.core.network.AuthMode
import app.nestbridge.app.context.identity.UserContext
import app.nestbridge.app.usecase.ClearUserContextUseCase
import app.nestbridge.app.usecase.GetUserContextUseCase
import app.nestbridge.app.usecase.LogoutUseCase
import app.nestbridge.feature.designcatalog.screen.DesignCatalogScreen
import app.nestbridge.feature.profile.screen.ProfileEvent
import app.nestbridge.feature.profile.screen.ProfileScreen
import app.nestbridge.feature.profile.screen.ProfileState
import app.nestbridge.domain.ping.usecase.PingUseCase
import app.nestbridge.app.usecase.PasskeyEnrollmentPromptUseCase
import com.karawek.core.auth.model.Passkey
import com.karawek.core.auth.usecase.GetAvailableAuthMethodsUseCase
import com.karawek.core.auth.usecase.RegisterPasskeyUseCase
import com.slack.circuit.codegen.annotations.CircuitInject
import com.slack.circuit.runtime.Navigator
import com.slack.circuit.runtime.presenter.Presenter
import kotlinx.coroutines.launch
import app.nestbridge.app.util.launchSafely
import me.tatarka.inject.annotations.Assisted
import me.tatarka.inject.annotations.Inject
import software.amazon.lastmile.kotlin.inject.anvil.AppScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow

@CircuitInject(
    screen = ProfileScreen::class,
    scope = AppScope::class,
)
class ProfilePresenter @Inject constructor(
    @Assisted private val navigator: Navigator,
    private val logoutUseCase: LogoutUseCase,
    private val getUserContextUseCase: GetUserContextUseCase,
    private val clearUserContextUseCase: ClearUserContextUseCase,
    private val pingUseCase: PingUseCase,
    private val registerPasskeyUseCase: RegisterPasskeyUseCase,
    private val getAvailableAuthMethods: GetAvailableAuthMethodsUseCase,
    private val passkeyEnrollmentPrompt: PasskeyEnrollmentPromptUseCase,
) : Presenter<ProfileState> {

    @Composable
    override fun present(): ProfileState {
        val scope = rememberCoroutineScope()
        var pingResult by remember { mutableStateOf<String?>(null) }
        var passkeyStatus by remember { mutableStateOf<String?>(null) }
        val passkeySupported by produceState(false) { value = getAvailableAuthMethods().contains(Passkey) }
        val userContextFlow by produceState<StateFlow<UserContext?>>(initialValue = MutableStateFlow<UserContext?>(null)) {
            value = getUserContextUseCase()
        }
        val userContext by userContextFlow.collectAsState()

        fun runPing(authMode: AuthMode) {
            scope.launch {
                runCatching {
                    pingUseCase(authMode)
                }.onSuccess { headers ->
                    pingResult = headers["Authorization"] ?: "No Authorization header"
                }.onFailure {
                    pingResult = it.message ?: "Error"
                }
            }
        }

        fun handleEvent(event: ProfileEvent) {
            when (event) {
                ProfileEvent.BackClicked -> navigator.pop()

                ProfileEvent.SetupPasskey ->
                    scope.launchSafely(onError = { passkeyStatus = "Couldn't set up passkey." }) {
                        registerPasskeyUseCase().fold(
                            onSuccess = {
                                passkeyStatus = "Passkey set up. You can use it to sign in next time."
                                passkeyEnrollmentPrompt.markPrompted()
                            },
                            onFailure = { passkeyStatus = it.message ?: "Couldn't set up passkey." },
                        )
                    }

                ProfileEvent.LogoutClicked ->
                    scope.launchSafely {
                        // Clearing the session triggers the auth gate in App(),
                        // which resets navigation to the login flow.
                        logoutUseCase()
                    }

                ProfileEvent.PingNoAuth ->
                    runPing(AuthMode.None)

                ProfileEvent.PingJwt ->
                    runPing(AuthMode.Jwt)

                ProfileEvent.PingSessionToken ->
                    runPing(AuthMode.SessionToken)

                ProfileEvent.ShowDesignCatalog ->
                    navigator.goTo(DesignCatalogScreen)
            }
        }

        return ProfileState(
            displayName = userContext?.userProfile?.displayName ?: "Guest",
            pingResult = pingResult,
            passkeySupported = passkeySupported,
            passkeyStatus = passkeyStatus,
            eventSink = ::handleEvent,
        )
    }
}
